import { useEffect } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { usePrefs } from "@/lib/store";
import { useTheme } from "@/lib/theme";
import { Home, History, Sparkles, Settings, RefreshCw, BarChart3 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { BrandHeader } from "./Brand";
import { WelcomeTour } from "./WelcomeTour";

const TABS = [
  { to: "/", label: "Check In", icon: Home, end: true },
  { to: "/history", label: "History", icon: History },
  { to: "/affirmations", label: "Affirmations", icon: Sparkles },
  { to: "/settings", label: "Settings", icon: Settings },
];

function SideNav() {
  const { user } = useAuth();
  return (
    <aside className="hidden md:flex fixed inset-y-0 left-0 w-60 flex-col gap-8 border-r border-t-border bg-t-surface px-5 py-7 z-20">
      <BrandHeader size={30} />
      <nav className="flex flex-col gap-1">
        {TABS.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} data-testid={`nav-${label.toLowerCase().replace(" ", "-")}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-2xl px-4 h-12 font-medium transition-colors ${isActive ? "bg-t-brand3 text-t-brand" : "text-t-on2 hover:bg-t-surface2"}`}>
            <Icon size={20} /> {label}
          </NavLink>
        ))}
        <NavLink to="/flip" data-testid="nav-flip"
          className="mt-3 flex items-center gap-3 rounded-2xl px-4 h-12 font-semibold bg-t-brand2 text-t-brand hover:brightness-[0.98]">
          <RefreshCw size={18} /> Flip the Script
        </NavLink>
        {user?.is_admin ? (
          <NavLink to="/admin" data-testid="nav-admin" className="flex items-center gap-3 rounded-2xl px-4 h-12 font-medium text-t-on2 hover:bg-t-surface2">
            <BarChart3 size={20} /> Admin analytics
          </NavLink>
        ) : null}
      </nav>
      <p className="mt-auto text-xs text-t-muted leading-relaxed">Not a crisis service. If you are in danger, call your local emergency number.</p>
    </aside>
  );
}

function BottomTabs() {
  return (
    <nav style={{ paddingBottom: "env(safe-area-inset-bottom)" }} className="md:hidden fixed bottom-0 inset-x-0 h-[calc(4rem+env(safe-area-inset-bottom))] bg-t-surface/95 backdrop-blur-xl border-t border-t-border flex z-20 no-print">
      {TABS.map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to} end={end} data-testid={`tab-${label.toLowerCase().replace(" ", "-")}`}
          className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors ${isActive ? "text-t-brand" : "text-t-muted"}`}>
          <Icon size={22} /> {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Shell() {
  const { data: prefs } = usePrefs();
  const { setScheme, setAccent } = useTheme();
  useEffect(() => {
    if (prefs?.scheme) setScheme(prefs.scheme);
    if (prefs?.accent) setAccent(prefs.accent);
  }, [prefs?.scheme, prefs?.accent, setScheme, setAccent]);
  return (
    <div className="min-h-screen">
      <SideNav />
      <main className="md:pl-60">
        <Outlet />
        <WelcomeTour />
      </main>
      <BottomTabs />
    </div>
  );
}
